import { useCallback, useEffect, useRef, useState } from "react";
import {
  calculateTooltipPosition,
  estimateTooltipDimensions,
  getViewportBounds,
  type PositionOptions,
} from "@utils/tooltipPositioning";

interface UseTooltipPositionOptions extends Omit<PositionOptions, "alignOffset"> {
  alignOffset?: number;
  portalRoot?: HTMLElement | null;
  isOpen: boolean;
  content?: string;
}

interface UseTooltipPositionReturn {
  tooltipStyle: React.CSSProperties;
  updatePosition: () => void;
  tooltipRef: React.RefObject<HTMLDivElement>;
}

export function useTooltipPosition(
  triggerRef: React.RefObject<HTMLElement>,
  options: UseTooltipPositionOptions
): UseTooltipPositionReturn {
  const {
    side = "top",
    align = "center",
    sideOffset = 8,
    alignOffset = 0,
    portalRoot,
    isOpen,
    content = "",
  } = options;

  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !isOpen) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportBounds = getViewportBounds(portalRoot);
    
    // Get container rect (portal root or document body)
    const container = portalRoot || document.body;
    const containerRect = container.getBoundingClientRect();

    // Get tooltip dimensions
    let tooltipDimensions;
    if (tooltipRef.current) {
      // Use actual dimensions if tooltip is rendered
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      tooltipDimensions = {
        width: tooltipRect.width,
        height: tooltipRect.height,
      };
    } else {
      // Estimate dimensions if tooltip isn't rendered yet
      tooltipDimensions = estimateTooltipDimensions(content);
    }

    const positionOptions: PositionOptions = {
      side,
      align,
      sideOffset,
      alignOffset,
    };

    const position = calculateTooltipPosition(
      triggerRect,
      tooltipDimensions,
      containerRect,
      viewportBounds,
      positionOptions
    );

    const newStyle: React.CSSProperties = {
      position: "absolute",
      top: `${position.top}px`,
      left: `${position.left}px`,
      transform: position.transform,
      transformOrigin: position.transformOrigin,
      width: `${position.width}px`, // Explicitly set width
      zIndex: 999,
    };

    setTooltipStyle(newStyle);
  }, [
    triggerRef,
    isOpen,
    side,
    align,
    sideOffset,
    alignOffset,
    portalRoot,
    content,
  ]);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        updatePosition();
      });
    }
  }, [isOpen, updatePosition]);

  // Set up observers to update position when things change
  useEffect(() => {
    if (!isOpen || !triggerRef.current) {
      return;
    }

    // Observe window resize
    const handleResize = () => {
      updatePosition();
    };

    // Observe scroll events on all scrollable ancestors
    const handleScroll = () => {
      updatePosition();
    };

    // Set up ResizeObserver for trigger element
    if (window.ResizeObserver) {
      resizeObserverRef.current = new ResizeObserver(() => {
        updatePosition();
      });
      resizeObserverRef.current.observe(triggerRef.current);
    }

    // Set up MutationObserver for DOM changes
    if (window.MutationObserver) {
      mutationObserverRef.current = new MutationObserver(() => {
        updatePosition();
      });
      mutationObserverRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });
    }

    // Add event listeners
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true); // Use capture to catch all scroll events

    // Find all scrollable ancestors and listen to their scroll events
    let element = triggerRef.current.parentElement;
    const scrollableElements: Element[] = [];
    
    while (element) {
      const computedStyle = window.getComputedStyle(element);
      const isScrollable = 
        computedStyle.overflow === "auto" ||
        computedStyle.overflow === "scroll" ||
        computedStyle.overflowX === "auto" ||
        computedStyle.overflowX === "scroll" ||
        computedStyle.overflowY === "auto" ||
        computedStyle.overflowY === "scroll";
      
      if (isScrollable) {
        scrollableElements.push(element);
        element.addEventListener("scroll", handleScroll);
      }
      
      element = element.parentElement;
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
      
      scrollableElements.forEach((el) => {
        el.removeEventListener("scroll", handleScroll);
      });

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
    };
  }, [isOpen, updatePosition, triggerRef]);

  return {
    tooltipStyle,
    updatePosition,
    tooltipRef,
  };
}
